import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

export const MarketCard = ({ title, volume, image, chance = 50, ticker, source, validations = 0, agentScore, onClick, disableClick = false }) => {
    const navigate = useNavigate();
    const [imgSrc, setImgSrc] = useState(image || "/assets/market-placeholder.png");

    const handleTrade = (e) => {
        if (disableClick) return;
        e.stopPropagation();
        if (onClick) {
            onClick();
            return;
        }
        // Navigate directly (wallet connect coming soon)
        navigate('/simulator');
    };

    return (
        <motion.div
            // UAT HIGH-004: Pump.fun-style entrance animation
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            // Enhanced hover effects
            whileHover={disableClick ? undefined : { scale: 1.02, y: -4 }}
            whileTap={disableClick ? undefined : { scale: 0.98 }}
            onClick={disableClick ? undefined : handleTrade}
            className={`group relative flex items-center gap-4 p-4 rounded-xl border border-white/5 bg-[#0a0a0b] transition-all overflow-hidden shadow-lg ${disableClick ? 'cursor-default' : 'hover:border-primary/50 hover:bg-white/[0.02] cursor-pointer hover:shadow-[0_8px_30px_rgba(57,230,106,0.2)]'}`}
        >
            {/* Top glow effect on hover */}
            {!disableClick && <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />}

            {/* Border glow on hover */}
            {!disableClick && <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-transparent via-primary/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />}

            {/* Minimal Icon */}
            <div className={`relative w-12 h-12 flex-shrink-0 rounded-lg overflow-hidden border border-white/10 transition-colors ${disableClick ? '' : 'group-hover:border-primary/50'}`}>
                <img
                    src={imgSrc}
                    alt={title}
                    className="h-full w-full object-cover opacity-80 group-hover:opacity-100 transition-opacity"
                    onError={() => setImgSrc('/assets/market-placeholder.png')}
                />
            </div>

            {/* Main Content */}
            <div className="flex-1 min-w-0 pr-2">
                <div className="flex items-center gap-2 mb-1">
                    <span className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider">{source || 'Polymarket'}</span>
                    {ticker && <span className="text-[10px] font-mono text-primary/80 px-1.5 py-0.5 rounded bg-primary/5 border border-primary/10 tracking-tighter">{ticker}</span>}
                </div>
                <h3 className="text-sm font-bold text-white leading-snug truncate group-hover:text-primary transition-colors">
                    {title}
                </h3>

                {/* Agent Validation Tag */}
                {validations > 0 && (
                    <div className="flex items-center gap-1.5 mt-1.5">
                        <div className="flex -space-x-1.5">
                            {[...Array(Math.min(3, validations))].map((_, i) => (
                                <div key={i} className="w-3.5 h-3.5 rounded-full border border-[#0a0a0b] bg-secondary/80 flex items-center justify-center">
                                    <span className="text-[6px] font-bold text-white">AI</span>
                                </div>
                            ))}
                        </div>
                        <span className="text-[9px] text-secondary font-bold uppercase tracking-wider">
                            {validations} Agent {validations === 1 ? 'Brain' : 'Brains'} Active
                        </span>
                    </div>
                )}
            </div>

            {/* Pump.fun Style Prediction Piller */}
            <div className="flex flex-col items-end gap-1">
                {/* Platform Prediction Badge */}
                {validations > 0 && agentScore !== undefined && (
                    <div className="mb-1 flex items-center gap-1">
                        <span className="text-[8px] font-mono text-secondary uppercase">Platform:</span>
                        <span className="text-[10px] font-bold text-white bg-secondary/10 px-1 rounded border border-secondary/20">
                            {Math.round(agentScore)}%
                        </span>
                    </div>
                )}

                {/* Shake Animation on Hover */}
                <motion.div
                    whileHover={{ rotate: [0, -5, 5, -5, 5, 0] }}
                    transition={{ duration: 0.4 }}
                    className="flex bg-white/5 rounded-lg border border-white/10 p-1 overflow-hidden backdrop-blur-sm"
                >
                    <div className={`px-2 py-1 rounded-md text-center transition-all ${chance > 50 ? 'bg-secondary text-black shadow-[0_0_10px_rgba(0,242,255,0.4)]' : 'text-muted-foreground'}`}>
                        <div className="text-[8px] font-bold uppercase tracking-wider">Yes</div>
                        <div className="text-xs font-black">{chance}%</div>
                    </div>
                    <div className={`px-2 py-1 rounded-md text-center transition-all ${chance <= 50 ? 'bg-primary text-white shadow-[0_0_10px_rgba(255,77,77,0.4)]' : 'text-muted-foreground'}`}>
                        <div className="text-[8px] font-bold uppercase tracking-wider">No</div>
                        <div className="text-xs font-black">{100 - chance}%</div>
                    </div>
                </motion.div>

                {/* UAT HIGH-006: 24h Volume Display - More Prominent */}
                <div className="text-right flex flex-col items-end mt-1">
                    <span className="text-[8px] font-mono text-muted-foreground uppercase tracking-widest">24h Volume</span>
                    <span className="text-sm font-bold text-primary">{typeof volume === 'number' ? `$${volume.toLocaleString()}` : volume}</span>
                </div>
            </div>
        </motion.div>
    );
};
